/**
 * Siren - Sound Manager for Tavern of Azoth
 *
 * Handles audio playback for alert sounds and music with caching support.
 * Provides smart alert system that respects user preferences.
 */

const NOTIFICATION_SOUNDS = {
    KING: 'King.mp3',
    SOFT: 'Soft.mp3',
    SUSPENCE: 'Suspence.mp3',
    BELL: 'Bell.mp3',
    PIANO: 'Piano.mp3',
    HARP: 'Harp.mp3',
    FROG: 'Frog.mp3',
    OWL: 'Owl.mp3',
    BRAAM: 'Braam.mp3',
    CELLO: 'Cello.mp3',
};

class Siren {
    static preferences = {
        enabled: true,
        soundFile: 'Harp.mp3'
    };

    static audioCache = new Map();
    static musicCache = new Map(); // Separate cache for music blobs
    static currentMusic = null;
    static musicVolume = 1.0;
    static effectsVolume = 1.0;
    static MUSIC_CACHE_NAME = 'tavern-music-cache-v1';

    /**
     * Initialize Siren with user preferences
     * @param {boolean} enabled - Whether alerts are enabled
     * @param {string} soundFile - The sound file to use for alerts
     */
    static init(enabled = true, soundFile = 'Harp.mp3') {
        this.preferences.enabled = enabled;
        this.preferences.soundFile = soundFile;

        // Preload the selected alert sound
        this.preload(soundFile);
    }

    /**
     * Update preferences
     * @param {boolean} enabled - Whether alerts are enabled
     * @param {string} soundFile - The sound file to use for alerts
     */
    static setPreferences(enabled, soundFile) {
        this.preferences.enabled = enabled;
        this.preferences.soundFile = soundFile;

        // Preload new sound if changed
        if (soundFile && !this.audioCache.has(soundFile)) {
            this.preload(soundFile);
        }
    }

    /**
     * Get current preferences
     * @returns {Object} Current preferences
     */
    static getPreferences() {
        return { ...this.preferences };
    }

    /**
     * Smart alert - Checks preferences and plays selected sound
     */
    static alert() {
        if (!this.preferences.enabled) {
            return;
        }

        this.play(this.preferences.soundFile);
    }

    /**
     * Play a sound effect directly (bypasses preferences)
     * @param {string} soundFile - The sound file to play (e.g., 'Bell.mp3')
     * @returns {Promise} Promise that resolves when sound starts playing
     */
    static async play(soundFile) {
        try {
            const audio = await this.getAudio(soundFile);
            const audioClone = audio.cloneNode();
            audioClone.volume = this.effectsVolume;

            return audioClone.play();
        } catch (error) {
            console.error('Failed to play sound:', soundFile, error);
        }
    }

    /**
     * Preload a sound file into cache
     * @param {string} soundFile - The sound file to preload
     * @returns {Promise} Promise that resolves when audio is loaded
     */
    static async preload(soundFile) {
        if (this.audioCache.has(soundFile)) {
            return this.audioCache.get(soundFile);
        }

        try {
            const audio = await this.loadAudio(soundFile);
            this.audioCache.set(soundFile, audio);
            return audio;
        } catch (error) {
            console.error('Failed to preload sound:', soundFile, error);
            throw error;
        }
    }

    /**
     * Get audio element (from cache or load)
     * @param {string} soundFile - The sound file to get
     * @returns {Promise<HTMLAudioElement>} The audio element
     */
    static async getAudio(soundFile) {
        if (this.audioCache.has(soundFile)) {
            return this.audioCache.get(soundFile);
        }

        return await this.preload(soundFile);
    }

    /**
     * Load audio file from server
     * @param {string} soundFile - The sound file to load
     * @returns {Promise<HTMLAudioElement>} The loaded audio element
     */
    static async loadAudio(soundFile) {
        const url = `/api/audio/file/notifications/${soundFile.toLowerCase()}`;

        // Try to use Cache API for persistent caching
        if ('caches' in window) {
            try {
                const cache = await caches.open('tavern-audio-cache-v1');
                let response = await cache.match(url);

                if (!response) {
                    response = await fetch(url);
                    if (response.ok) {
                        await cache.put(url, response.clone());
                    }
                }

                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                const audio = new Audio(objectUrl);
                await audio.load();

                return audio;
            } catch (error) {
                console.warn('Cache API failed, falling back to direct load:', error);
            }
        }

        // Fallback to direct loading
        const audio = new Audio(url);
        await new Promise((resolve, reject) => {
            audio.addEventListener('canplaythrough', resolve, { once: true });
            audio.addEventListener('error', reject, { once: true });
        });

        return audio;
    }

    /**
     * Start playing music (looping)
     * @param {string} musicUrl - URL or path to music file
     * @param {boolean} loop - Whether to loop the music
     */
    static async startMusic(musicUrl, loop = true) {
        this.stopMusic();

        try {
            this.currentMusic = new Audio(musicUrl);
            this.currentMusic.loop = loop;
            this.currentMusic.volume = this.musicVolume;
            await this.currentMusic.play();
        } catch (error) {
            console.error('Failed to start music:', error);
        }
    }

    /**
     * Pause current music
     */
    static pauseMusic() {
        if (this.currentMusic && !this.currentMusic.paused) {
            this.currentMusic.pause();
        }
    }

    /**
     * Resume paused music
     */
    static resumeMusic() {
        if (this.currentMusic && this.currentMusic.paused) {
            this.currentMusic.play().catch(error => {
                console.error('Failed to resume music:', error);
            });
        }
    }

    /**
     * Stop current music
     */
    static stopMusic() {
        if (this.currentMusic) {
            this.currentMusic.pause();
            this.currentMusic.currentTime = 0;
            this.currentMusic = null;
        }
    }

    /**
     * Set music volume
     * @param {number} volume - Volume level (0.0 to 1.0)
     */
    static setMusicVolume(volume) {
        this.musicVolume = Math.max(0, Math.min(1, volume));
        if (this.currentMusic) {
            this.currentMusic.volume = this.musicVolume;
        }
    }

    /**
     * Set effects volume
     * @param {number} volume - Volume level (0.0 to 1.0)
     */
    static setEffectsVolume(volume) {
        this.effectsVolume = Math.max(0, Math.min(1, volume));
    }

    /**
     * Check if a sound file is cached
     * @param {string} soundFile - The sound file to check
     * @returns {Promise<boolean>} True if file is cached
     */
    static async isCached(soundFile) {
        // Check in-memory cache first
        if (this.audioCache.has(soundFile)) {
            return true;
        }

        // Check Cache API
        if ('caches' in window) {
            try {
                const url = `/api/audio/file/notifications/${soundFile.toLowerCase()}`;
                const cache = await caches.open('tavern-audio-cache-v1');
                const response = await cache.match(url);
                return !!response;
            } catch (error) {
                console.error('Failed to check cache:', error);
                return false;
            }
        }

        return false;
    }

    // ============================================
    // Music Caching Methods
    // ============================================

    /**
     * Check if a music track is cached
     * @param {string} musicId - The music ID to check
     * @returns {Promise<boolean>} True if music is cached
     */
    static async isMusicCached(musicId) {
        // Check in-memory cache first
        if (this.musicCache.has(musicId)) {
            return true;
        }

        // Check Cache API
        if ('caches' in window) {
            try {
                const url = `/api/music/${musicId}/stream`;
                const cache = await caches.open(this.MUSIC_CACHE_NAME);
                const response = await cache.match(url);
                return !!response;
            } catch (error) {
                console.error('Failed to check music cache:', error);
                return false;
            }
        }

        return false;
    }

    /**
     * Get music blob from cache or fetch from server
     * @param {string} musicId - The music ID to get
     * @returns {Promise<{blob: Blob, fromCache: boolean}>} The music blob and whether it was from cache
     */
    static async getMusicBlob(musicId) {
        const url = `/api/music/${musicId}/stream`;

        // Check in-memory cache first
        if (this.musicCache.has(musicId)) {
            return { blob: this.musicCache.get(musicId), fromCache: true };
        }

        // Try Cache API
        if ('caches' in window) {
            try {
                const cache = await caches.open(this.MUSIC_CACHE_NAME);
                const response = await cache.match(url);

                if (response) {
                    const blob = await response.blob();
                    // Also store in memory for faster subsequent access
                    this.musicCache.set(musicId, blob);
                    return { blob, fromCache: true };
                }
            } catch (error) {
                console.warn('Cache API read failed, will fetch from server:', error);
            }
        }

        // Fetch from server
        try {
            const response = await fetch(url, { credentials: 'same-origin' });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();

            // Store in caches
            this.musicCache.set(musicId, blob);

            if ('caches' in window) {
                try {
                    const cache = await caches.open(this.MUSIC_CACHE_NAME);
                    await cache.put(url, new Response(blob.slice()));
                } catch (cacheError) {
                    console.warn('Failed to write to Cache API:', cacheError);
                }
            }

            return { blob, fromCache: false };
        } catch (error) {
            console.error('Failed to fetch music:', musicId, error);
            throw error;
        }
    }

    /**
     * Preload a music track into cache
     * @param {string} musicId - The music ID to preload
     * @returns {Promise<void>}
     */
    static async preloadMusic(musicId) {
        const isCached = await this.isMusicCached(musicId);
        if (isCached) {
            return;
        }

        await this.getMusicBlob(musicId);
    }

    /**
     * Clear audio cache
     */
    static clearCache() {
        const memoryCount = this.audioCache.size;
        const musicMemoryCount = this.musicCache.size;
        this.audioCache.clear();
        this.musicCache.clear();
        console.log(`[Siren] 🗑️ Cleared in-memory cache (${memoryCount} audio, ${musicMemoryCount} music files)`);

        if ('caches' in window) {
            Promise.all([
                caches.delete('tavern-audio-cache-v1'),
                caches.delete(this.MUSIC_CACHE_NAME)
            ]).then(() => {
                console.log('[Siren] 🗑️ Cleared Cache API storage (audio + music)');
            }).catch(error => {
                console.error('Failed to clear cache:', error);
            });
        }
    }

    /**
     * Dump current cache state to console
     */
    static async dumpCache() {
        console.log('═══════════════════════════════════════════');
        console.log('🔊 Siren Audio Cache State');
        console.log('═══════════════════════════════════════════');

        // In-memory cache
        console.log('\n📦 In-Memory Cache:');
        if (this.audioCache.size === 0) {
            console.log('  (empty)');
        } else {
            this.audioCache.forEach((audio, filename) => {
                console.log(`  ✓ ${filename}`);
            });
        }
        console.log(`  Total: ${this.audioCache.size} files`);

        // Cache API
        if ('caches' in window) {
            try {
                const cache = await caches.open('tavern-audio-cache-v1');
                const keys = await cache.keys();

                console.log('\n💾 Cache API (Persistent):');
                if (keys.length === 0) {
                    console.log('  (empty)');
                } else {
                    keys.forEach(request => {
                        const filename = request.url.split('/').pop();
                        console.log(`  ✓ ${filename}`);
                    });
                }
                console.log(`  Total: ${keys.length} files`);
            } catch (error) {
                console.error('  ✗ Failed to read Cache API:', error);
            }
        } else {
            console.log('\n💾 Cache API: Not available');
        }

        // Available sounds
        console.log('\n🎵 Available Notification Sounds:');
        Object.entries(NOTIFICATION_SOUNDS).forEach(([key, filename]) => {
            const inMemory = this.audioCache.has(filename);
            const status = inMemory ? '✓' : '○';
            console.log(`  ${status} ${key}: ${filename}`);
        });

        // Current preferences
        console.log('\n⚙️ Current Preferences:');
        console.log(`  Enabled: ${this.preferences.enabled}`);
        console.log(`  Sound: ${this.preferences.soundFile}`);
        console.log(`  Music Volume: ${this.musicVolume}`);
        console.log(`  Effects Volume: ${this.effectsVolume}`);

        console.log('\n═══════════════════════════════════════════\n');
    }

    /**
     * Initialize debug keyboard shortcuts
     */
    static initDebugShortcuts() {
        if (typeof window === 'undefined') return;

        document.addEventListener('keydown', (e) => {
            // Ctrl+Shift+A = Dump audio cache
            if (e.ctrlKey && e.shiftKey && e.key === 'A') {
                e.preventDefault();
                console.log('[Siren] 🔑 Keyboard shortcut: Dump audio cache');
                this.dumpCache();
            }

            // Ctrl+Shift+C = Clear audio cache
            if (e.ctrlKey && e.shiftKey && e.key === 'C') {
                e.preventDefault();
                console.log('[Siren] 🔑 Keyboard shortcut: Clear audio cache');
                this.clearCache();
            }
        });

        console.log('[Siren] ⌨️ Debug shortcuts enabled:');
        console.log('  Ctrl+Shift+A = Dump audio cache state');
        console.log('  Ctrl+Shift+C = Clear audio cache');
    }

    /**
     * Get list of available notification sounds
     * @returns {Object} Object with sound file names
     */
    static getAvailableSounds() {
        return { ...NOTIFICATION_SOUNDS };
    }
}

export default Siren;
export { NOTIFICATION_SOUNDS };
